<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FollowUpSequenceCard from 'dashboard/components-next/Campaigns/Advanced/FollowUp/FollowUpSequenceCard.vue';
import FollowUpFormDialog from 'dashboard/components-next/Campaigns/Advanced/FollowUp/FollowUpFormDialog.vue';
import EnrollContactsDialog from 'dashboard/components-next/Campaigns/Advanced/FollowUp/EnrollContactsDialog.vue';

import FollowUpSequencesAPI from 'dashboard/api/followUpSequences';

const { t } = useI18n();
const router = useRouter();

const sequences = ref([]);
const isLoading = ref(false);

// Dialog refs
const formDialogRef = ref(null);
const editingSequence = ref(null);

const fetchSequences = async () => {
  isLoading.value = true;
  try {
    const response = await FollowUpSequencesAPI.get();
    sequences.value = response.data || [];
  } catch (error) {
    useAlert(t('CAMPAIGN.FOLLOW_UP.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openCreateDialog = () => {
  editingSequence.value = null;
  formDialogRef.value?.open();
};

const openEditDialog = sequence => {
  editingSequence.value = sequence;
  formDialogRef.value?.open();
};

const handleDialogClose = () => {
  editingSequence.value = null;
};

const handleSaveSequence = async payload => {
  try {
    if (payload.id) {
      await FollowUpSequencesAPI.update(payload.id, payload);
      useAlert(t('CAMPAIGN.FOLLOW_UP.UPDATE_SUCCESS'));
    } else {
      await FollowUpSequencesAPI.create(payload);
      useAlert(t('CAMPAIGN.FOLLOW_UP.CREATE_SUCCESS'));
    }
    fetchSequences();
  } catch (error) {
    useAlert(
      payload.id
        ? t('CAMPAIGN.FOLLOW_UP.UPDATE_ERROR')
        : t('CAMPAIGN.FOLLOW_UP.CREATE_ERROR')
    );
  }
};

const enrollDialogRef = ref(null);
const enrollSequenceId = ref(null);

const handleView = sequence => {
  router.push({
    name: 'campaigns_follow_up_detail',
    params: { sequenceId: sequence.id },
  });
};

const handleEnroll = sequence => {
  enrollSequenceId.value = sequence.id;
  enrollDialogRef.value?.open();
};

const handleDelete = async sequence => {
  if (!window.confirm(t('CAMPAIGN.FOLLOW_UP.DELETE_CONFIRM'))) return;

  try {
    await FollowUpSequencesAPI.delete(sequence.id);
    useAlert(t('CAMPAIGN.FOLLOW_UP.DELETE_SUCCESS'));
    fetchSequences();
  } catch (error) {
    useAlert(t('CAMPAIGN.FOLLOW_UP.DELETE_ERROR'));
  }
};

onMounted(() => {
  fetchSequences();
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.FOLLOW_UP.TITLE')"
    :button-label="t('CAMPAIGN.FOLLOW_UP.CREATE')"
    @click="openCreateDialog"
  >
    <Spinner v-if="isLoading" />

    <div v-else class="space-y-4">
      <FollowUpSequenceCard
        v-for="sequence in sequences"
        :key="sequence.id"
        :sequence="sequence"
        @view="handleView"
        @edit="openEditDialog"
        @enroll="handleEnroll"
        @delete="handleDelete"
      />

      <div v-if="sequences.length === 0" class="py-12 text-center">
        <div class="i-lucide-repeat mx-auto size-12 text-n-slate-8" />
        <h3 class="mt-4 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.FOLLOW_UP.EMPTY') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.FOLLOW_UP.EMPTY_HINT') }}
        </p>
        <Button class="mt-4" icon="i-lucide-plus" @click="openCreateDialog">
          {{ t('CAMPAIGN.FOLLOW_UP.CREATE_BTN') }}
        </Button>
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <FollowUpFormDialog
      ref="formDialogRef"
      :sequence="editingSequence"
      @close="handleDialogClose"
      @save="handleSaveSequence"
    />

    <!-- Enroll Contacts Dialog -->
    <EnrollContactsDialog
      v-if="enrollSequenceId"
      ref="enrollDialogRef"
      :sequence-id="enrollSequenceId"
      @enrolled="fetchSequences"
    />
  </CampaignLayout>
</template>
